import { IFormattedDate } from '@/types';
import { months, monthsShort, week } from '@/constants/date';
import { ISetValue } from '@models/Forms/IForms';
import { parseRuDate, parseRuDateTime } from '@utils/date';
import { toLocalDate } from '@srm/ui/lib/toLocalDate';
import { appendTraceId, TRACE_ID_LABEL, traceIdFromText } from '@srm/core/errors';
import { FieldErrors } from 'react-hook-form';
import { IFieldElem } from '@models/IFormData';

/**
 * Функция для добавления окончаний к месяцам
 * @param value - имя месяца
 *
 */
export const getMonthName = (value: string) => {
    if (value) {
        const arr: string[] = value.split('');
        let result;

        if (months.ru.indexOf(value) !== -1) {
            arr.splice(arr.length - 1, 1, 'я');
            result = arr.join('');
            return result;
        }

        arr.push('а');
        result = arr.join('');
        return result;
    }

    return '';
};
export const getValueByPath = (object: any, path: string) => {
    let parts = path.split('.');
    let current = object;
    parts.forEach((element) => {
        if (element.indexOf('[') > -1) {
            let index = +element.replace('[', '').replace(']', '');
            current = current[index];
        } else {
            current = current[element];
        }
    });
    return current;
};

const isObject = (v: any) => v !== null && typeof v === 'object';
const isDate = (v: any): v is Date => v instanceof Date;
export const isISODate = (v: any): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v);
const toEpoch = (v: any): number | null => {
    if (isDate(v)) return v.getTime();
    if (isISODate(v)) {
        const t = Date.parse(v);
        return Number.isNaN(t) ? null : t;
    }
    return null;
};

export const deepEqual = (object1: any, object2: any): boolean => {
    // Обработка undefined / null симметрично
    if (object1 == undefined || object2 == undefined) {
        return object1 == undefined && object2 == undefined;
    }

    const sysNames = ['_dataByKeyMap', '_dataByKeyMapLength'];

    const keys1 = Object.keys(object1).filter((x) => sysNames.indexOf(x) === -1);
    const keys2 = Object.keys(object2).filter((x) => sysNames.indexOf(x) === -1);
    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
        const val1 = object1[key];
        const val2 = object2[key];

        // 1) Даты и дата-подобные значения (Date | ISO-строки | timestamp)
        const t1 = toEpoch(val1);
        const t2 = toEpoch(val2);
        if (t1 !== null && t2 !== null) {
            if (t1 !== t2) return false;
            continue; // равны по времени - идём дальше
        }

        // 2) Объекты/массивы - рекурсивно
        const areObjects = isObject(val1) && isObject(val2);
        if (areObjects) {
            if (!deepEqual(val1, val2)) return false;
            continue;
        }

        // 3) Примитивы - строгое сравнение
        if (val1 !== val2) return false;
    }
    return true;
};

/** Добавить нули */
export const addLeadingZeros = (number: number, length = 2) => {
    return ('0'.repeat(length) + number).slice(-length);
};

/**
 * Функция для форматирования даты
 *  @param date - дата, которую нужно преобразовать
 *
 */
export const formatDate = (date: string | number | undefined): IFormattedDate => {
    if (!date) {
        date = Date.now();
    }

    const tempDate = new Date(date);
    const month = addLeadingZeros(tempDate.getMonth() + 1);
    const monthLong = getMonthName(months.ru[tempDate.getMonth()]);
    const monthName = months.ru[tempDate.getMonth()];
    const monthShort = monthsShort[tempDate.getMonth()];

    const dayOfMonth = addLeadingZeros(tempDate.getDate());
    const dayOfWeek = week[tempDate.getDay()];
    const tempHour = tempDate.getHours();
    const hour = addLeadingZeros(tempHour);
    const tempMinutes = tempDate.getMinutes();
    const minutes = addLeadingZeros(tempMinutes);
    const year = tempDate.getFullYear();

    return {
        month,
        monthLong,
        monthShort,
        monthName,
        dayOfMonth,
        dayOfWeek,
        hour,
        minutes,
        year,
        date: `${dayOfMonth}.${month}.${year}`,
        time: `${hour}:${minutes}`,
    };
};

/**
 * Функция обрезки текста
 * @param text - строка, которую нужно сократить
 * @param n - количество видимых символов
 * @param symbol - символ, который нужно поставить в конце строки
 */
export const getShortString = (text: string, n = 50, symbol = '...') => {
    return n > text.length ? text : `${text.slice(0, n)}${symbol}`;
};

/**
 * Обрезка текста отказа: режется только текст, код обращения в конце (`appendTraceId`) остаётся целиком — по нему
 * ищут в поддержке.
 */
export const getShortErrorString = (error: string, n: number) => {
    const traceId = traceIdFromText(error);
    if (!traceId) return getShortString(error, n);
    const text = error.slice(0, error.lastIndexOf(`${TRACE_ID_LABEL}: ${traceId}`)).trimEnd();
    return appendTraceId(getShortString(text, n), traceId, '\n');
};

export const getActivityIdFromCompositeId = (compositeId: string): string => {
    let parts = compositeId.split('_');

    return parts[parts.length - 1];
};

export const getDocIdFromLinkedDocId = (linkedDocId: string): string => {
    let parts = linkedDocId.split('/');
    if (parts.length === 1) return linkedDocId;
    if (parts.length === 2) return parts[1];

    throw new Error(`Не удалось распарсить ид документа и родители линка ${parts}`);
};

export const getParentDocIdFromLinkedDocId = (linkedDocId: string): string => {
    let parts = linkedDocId.split('/');
    if (parts.length === 1) return linkedDocId;
    if (parts.length === 2) return parts[0];

    throw new Error(`Не удалось распарсить ид документа и родители линка ${parts}`);
};

export const getAcceptExtensionString = (extensions: string) => {
    if (!extensions.trim()) return '*';
    return extensions
        .split(',')
        .map((ext) => (ext.startsWith('.') ? ext.trim() : `.${ext.trim()}`))
        .join(',');
};

export const groupByToArray = function (list: any, keyGetter: any) {
    const map = new Map();
    list.forEach((item: any) => {
        const key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
            map.set(key, [item]);
        } else {
            collection.push(item);
        }
    });

    return Array.from(map, function (item) {
        return { key: item[0], value: item[1] };
    });
};

/**
 * Вернуть строковое значение из числа или строки.
 *
 * @param key Значение ключа
 */
export const stringifyKey = (key: string | number) => {
    return `${key}`;
};

export const checkFilesSize = (files: File[], maxSize: number | undefined): boolean => {
    if (!maxSize) return true;

    if (!files || files.length === 0) return true;

    return (
        files.reduce((sizeSum, file) => {
            return sizeSum + (file ? file.size : 0);
        }, 0) <= maxSize
    );
};

// toLocaleString с опциями создаёт новый Intl.NumberFormat на каждый вызов —
// в гридах цена форматируется на каждую ячейку, держим форматтеры по валюте
const priceFormatters = new Map<string, Intl.NumberFormat>();

export const formatPrice = (price: number | null | undefined, currencyCode: string): string => {
    try {
        if (price === null || price === undefined) return '';

        const currency = currencyCode?.trim();
        let formatter = priceFormatters.get(currency);
        if (!formatter) {
            formatter = new Intl.NumberFormat('ru-RU', { style: 'currency', currency });
            priceFormatters.set(currency, formatter);
        }
        return formatter.format(price);
    } catch (ex) {
        return `${price?.toString() ?? ''} ${currencyCode || ''}`;
    }
};

export function declensionOfSymbol(count: number) {
    const cases = [2, 0, 1, 1, 1, 2];
    const titles = ['символ', 'символа', 'символов'];
    return titles[count % 100 > 4 && count % 100 < 20 ? 2 : cases[count % 10 < 5 ? count % 10 : 5]];
}

/**
 * Функция валидации email
 * @param value email
 */
export const validateEmail = (value: string) => {
    const EMAIL_REGEX =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return EMAIL_REGEX.test(value.toLowerCase()) ? true : 'Некорректный email';
};

export const setValueTyped = (val: any, el: ISetValue) => {
    if (el.type) {
        switch (el.type) {
            case 'double':
            case 'integer': {
                try {
                    if (typeof val == 'string' && val.indexOf(',')) {
                        val = val.replace(',', '.');
                    }
                    val = +val;
                    if (isNaN(val)) {
                        val = 0;
                    }
                } catch (error) {
                    val = 0;
                }
                break;
            }
            case 'json': {
                val = JSON.parse(val);
                break;
            }
            case 'date': {
                try {
                    let parseVal = new Date(val);
                    if (!isNaN(parseVal.getTime()) && parseVal.getTime() > 0) {
                        val = parseVal;
                    } else {
                        throw new Error('parse Date');
                    }
                } catch (error) {
                    let parts = val.split(' ');
                    if (parts.length > 1) {
                        val = parseRuDateTime(val);
                    } else {
                        const parsed = parseRuDate(val);
                        if (parsed) {
                            val = parsed;
                        } else {
                            const fallback = toLocalDate(val);
                            if (!isNaN(fallback.getTime())) {
                                val = fallback;
                            } else {
                                val = null;
                            }
                        }
                    }
                }
                break;
            }
            default:
                break;
        }
    }
    return val;
};

export const docInvalidFormHandler = (errors: FieldErrors<any>, fieldsData: Record<string, IFieldElem>) => {
    Object.keys(fieldsData).forEach((key) => {
        const fieldName = fieldsData[key].name;
        let curElm: any = errors;
        const errorField = curElm.fields.find((field: any) => field?.value?.ref.name === fieldName);
        if (errorField) {
            console.log(`Ошибка валидации поля: ${key} (${errorField.value.ref.name}). Подробности ниже.`);
            console.log(errorField);
        }
    });
};

/** FNV-1a — быстрый не-криптографический хэш строки (32-bit). */
export const fnv1aHash = (str: string): number => {
    let hash = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash = (hash * 0x01000193) | 0;
    }
    return hash;
};
